import React from 'react';
import { Button } from '@saleshandy/design-system';
import PhoneNumberListSearch from '../phone-number-list-search';
import Icon from '../../../../../../../../shared/design-system/components/atoms/icon';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import {
  ForwardIcon,
  LabelIcon,
  VoiceMailIcon,
  CallRecordingIcon,
} from '../../../../../../../../shared/svg';
import { IProps } from './types';
import { UserPlus } from '../../../../../../../../shared/svg/user-plus';
import hasPermission from '../../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../../shared/utils/access-control/enums/permissions';

const PhoneNumberListHeader: React.FC<IProps> = ({
  enableBulkActions = false,
  search,
  onSearchChange,
  onFilterClick,
  onPurchaseClick,
  activeFilterCount = 0,
  onAssignClick,
  onAssignLabelClick,
  onEnableCallRecordingClick,
  onEnableVoicemailGreetingClick,
  onSetCallForwardingRuleClick,
  onDialerSettingsClick,
}) => {
  const handleFilterClick = () => {
    onFilterClick?.();
  };

  const handlePurchaseClick = () => {
    onPurchaseClick?.();
  };

  const handleAssignClick = () => {
    if (enableBulkActions) {
      onAssignClick?.();
    }
  };

  const handleAssignLabelClick = () => {
    if (enableBulkActions) {
      onAssignLabelClick?.();
    }
  };

  const handleEnableCallRecordingClick = () => {
    if (enableBulkActions) {
      onEnableCallRecordingClick?.();
    }
  };

  const handleEnableVoicemailGreetingClick = () => {
    if (enableBulkActions) {
      onEnableVoicemailGreetingClick?.();
    }
  };

  const handleSetCallForwardingRuleClick = () => {
    if (enableBulkActions) {
      onSetCallForwardingRuleClick?.();
    }
  };

  return (
    <div className="phone-number-list-header">
      <div className="phone-number-list-header__left">
        <div className="phone-number-list-search">
          <PhoneNumberListSearch search={search} onChange={onSearchChange} />
        </div>
        <OverlayTooltip text="Filter" placement={Placement.Bottom}>
          <div className="icon-container position-relative">
            <div
              className="phone-number-list-header__icon-button"
              {...accessibleOnClick(handleFilterClick)}
            >
              <Icon identifier="filter" className="icon" />
            </div>
            {activeFilterCount > 0 && <span className="filter-count-badge" />}
          </div>
        </OverlayTooltip>
        <div className="phone-number-list-header__divider" />
        {hasPermission(Permissions.PHONE_NUMBER_ASSIGNMENT_UPDATE) && (
          <OverlayTooltip text="Assign numbers to" placement={Placement.Bottom}>
            <div
              className={`phone-number-list-header__icon-button phone-number-list-header__icon-button--${
                enableBulkActions ? '' : 'disabled'
              }`}
              {...accessibleOnClick(handleAssignClick)}
            >
              <UserPlus className="icon" />
            </div>
          </OverlayTooltip>
        )}
        <OverlayTooltip
          text="Set Call Forwarding Rule"
          placement={Placement.Bottom}
        >
          <div
            className={`phone-number-list-header__icon-button phone-number-list-header__icon-button--${
              enableBulkActions ? '' : 'disabled'
            }`}
            {...accessibleOnClick(handleSetCallForwardingRuleClick)}
          >
            <ForwardIcon width={20} height={20} className="icon" />
          </div>
        </OverlayTooltip>
        <OverlayTooltip
          text="Assign Phone Number Label"
          placement={Placement.Bottom}
        >
          <div
            className={`phone-number-list-header__icon-button phone-number-list-header__icon-button--${
              enableBulkActions ? '' : 'disabled'
            }`}
            {...accessibleOnClick(handleAssignLabelClick)}
          >
            <LabelIcon width={20} height={20} className="icon" />
          </div>
        </OverlayTooltip>
        <OverlayTooltip
          text="Set Call Recording"
          placement={Placement.Bottom}
        >
          <div
            className={`phone-number-list-header__icon-button phone-number-list-header__icon-button--${
              enableBulkActions ? '' : 'disabled'
            }`}
            {...accessibleOnClick(handleEnableCallRecordingClick)}
          >
            <CallRecordingIcon width={20} height={20} className="icon" />
          </div>
        </OverlayTooltip>
        <OverlayTooltip
          text="Set Voicemail Greeting"
          placement={Placement.Bottom}
        >
          <div
            className={`phone-number-list-header__icon-button phone-number-list-header__icon-button--${
              enableBulkActions ? '' : 'disabled'
            }`}
            {...accessibleOnClick(handleEnableVoicemailGreetingClick)}
          >
            <VoiceMailIcon width={20} height={20} className="icon" />
          </div>
        </OverlayTooltip>
      </div>
      <div className="phone-number-list-header__right">
        {hasPermission(Permissions.ADMIN_SETTING_READ) && (
          <OverlayTooltip text="Dialer Settings" placement={Placement.Bottom}>
            <div
              className="phone-number-list-header__icon-button phone-number-list-header__icon-button--credit"
              {...accessibleOnClick(onDialerSettingsClick)}
            >
              <Icon identifier="setting" className="icon" />
            </div>
          </OverlayTooltip>
        )}
        {hasPermission(Permissions.PHONE_NUMBER_PURCHASE_READ) && (
          <>
            <div className="phone-number-list-header__divider" />
            <Button
              className="phone-number-list-header__purchase-button"
              {...accessibleOnClick(handlePurchaseClick)}
            >
              Purchase Number
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PhoneNumberListHeader;
